import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import HeroCanvas from "./HeroCanvas";
import { TID } from "../lib/testIds";

export default function Hero({ Canvas: CanvasComp = HeroCanvas, eyebrow = "Available · 2 client slots — Q1" }) {
  const { scrollYProgress } = useScroll();
  const yText = useTransform(scrollYProgress, [0, 0.2], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.18], [1, 0]);

  const words = "Engineering the Impossible.".split(" ");

  return (
    <section
      id="top"
      data-testid={TID.hero.section}
      className="relative min-h-[100svh] w-full overflow-hidden grain"
    >
      <CanvasComp />

      {/* Top eyebrow */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.8 }}
        className="hidden md:flex absolute top-28 md:top-32 left-6 md:left-10 z-10 items-center gap-3"
      >
        <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.7)]" />
        <span className="text-[11px] tracking-[0.28em] uppercase text-muted-x font-mono-x">
          Available · 2 client slots — Q1
        </span>
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-44 md:pt-56 pb-24"
      >
        <h1
          data-testid={TID.hero.headline}
          className="font-display text-[14vw] leading-[0.88] md:text-[9.5rem] lg:text-[11rem] font-bold tracking-[-0.05em] text-primary-x max-w-6xl text-balance"
        >
          {words.map((w, i) => (
            <motion.span
              key={i}
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{
                delay: 0.1 + i * 0.08,
                duration: 0.9,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="inline-block mr-[0.18em]"
            >
              {w === "Impossible." ? (
                <span className="italic font-medium bg-gradient-to-br from-primary-x via-secondary-x to-muted-x bg-clip-text text-transparent">
                  {w}
                </span>
              ) : (
                w
              )}
            </motion.span>
          ))}
        </h1>

        <div className="mt-12 md:mt-16 grid md:grid-cols-12 gap-8 items-end">
          <motion.p
            data-testid={TID.hero.subtitle}
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="md:col-span-6 text-base md:text-lg text-secondary-x leading-relaxed max-w-xl"
          >
            Ulties is a boutique software studio crafting{" "}
            <span className="text-primary-x">web, mobile & desktop apps</span>,
            production-grade <span className="text-primary-x">AI agents</span> and
            <span className="text-primary-x"> cyber‑security</span> systems for
            ambitious teams.
          </motion.p>

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="md:col-span-6 flex flex-wrap gap-3 md:justify-end"
          >
            <Link
              to="/work"
              data-testid={TID.hero.ctaSecondary}
              className="group inline-flex items-center gap-2 rounded-full border border-hairline-strong bg-glass backdrop-blur-xl px-6 py-3 text-sm text-primary-x hover-glass transition-colors"
            >
              View Work
              <span className="inline-block transition-transform group-hover:translate-x-0.5">→</span>
            </Link>
            <Link
              to="/contact"
              data-testid={TID.hero.ctaPrimary}
              className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium hover:opacity-90 transition-opacity"
              style={{ background: "var(--text-primary)", color: "var(--canvas)" }}
            >
              Start a Project
              <span className="inline-block transition-transform group-hover:translate-x-0.5">→</span>
            </Link>
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom Meta strip */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1.1, duration: 0.8 }}
        className="absolute bottom-6 md:bottom-10 left-6 md:left-10 right-6 md:right-10 z-10 flex items-end justify-between text-[11px] tracking-[0.2em] uppercase text-muted-x font-mono-x"
      >
        <div className="flex items-center gap-6">
          <span>EST. 2019</span>
          <span className="hidden md:inline">Remote · Worldwide</span>
        </div>
        <div className="flex items-center gap-2 text-secondary-x">
          <span>Scroll</span>
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          >
            ↓
          </motion.span>
        </div>
      </motion.div>
    </section>
  );
}
